import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  TextField,
  Grid,
  IconButton
} from '@mui/material';
import PieChartIcon from '@mui/icons-material/PieChart';
import CloseIcon from '@mui/icons-material/Close';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useApi from '../../hooks/useApi';
import branding from '../../styles/branding';
import { formatBusinessText } from '../../utils/textFormatting';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';
import './BuildPieChart.css';

const COLORS = [
  'rgb(64, 121, 141)',
  'rgb(209, 185, 167)',
  'rgb(149, 198, 213)',
  'rgb(145, 89, 145)',
  'rgb(165, 198, 165)',
  'rgb(125, 111, 100)',
  'rgb(170, 188, 244)',
  'rgb(73, 119, 73)',
  'rgb(208, 181, 208)',
  'rgb(102, 117, 121)'
];

const BuildPieChart = ({ open, onClose, onAddToDashboard, jobId, initialData, editWidgetId, onUpdateCard }) => {
  const [chartData, setChartData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showValueType, setShowValueType] = useState('percentage');
  const [decimalPoints, setDecimalPoints] = useState(1);
  const [customColors, setCustomColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [widgetName, setWidgetName] = useState('');
  const api = useApi();
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    if (open) {
      if (initialData?.widgetName) setWidgetName(initialData.widgetName);
      if (initialData?.query) {
        try {
          const q = JSON.parse(initialData.query);
          if (q.showValueType) setShowValueType(q.showValueType);
          if (q.decimalPoints !== undefined) setDecimalPoints(q.decimalPoints);
          if (q.customColors) setCustomColors(q.customColors);
        } catch (e) {}
      }
      fetchChartData();
    }
  }, [open]);

  const columnKey = (c) => String(c).trim().toLowerCase();
  const findColumn = (list, name) =>
    list.find((c) => columnKey(c) === columnKey(name));

  const fetchChartData = async () => {
    if (!jobId) {
      setError('JobID not found. Please try again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all data for chart rendering (not just preview)
      const response = await api.get(`/preview/${jobId}?all=true`);
      
      if (response.data?.success) {
        const data = response.data.data || {};
        // Preview endpoint returns 'previewData' not 'rows'
        setChartData(data.previewData || data.rows || []);
        const columnsList = data.columns?.map((col) => col.name || col) || [];
        setColumns(columnsList);

        // Do not reset to the first column when a slower/duplicate preview response
        // arrives after the user already picked a category (Strict Mode or overlapping fetches).
        setSelectedCategory((prev) => {
          if (!columnsList.length) return '';
          if (prev && findColumn(columnsList, prev)) return findColumn(columnsList, prev);
          const fromInitial = initialData?.selectedColumns?.[0];
          if (fromInitial && findColumn(columnsList, fromInitial)) {
            return findColumn(columnsList, fromInitial);
          }
          return columnsList[0];
        });
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setError(getApiErrorMessage(error, 'Failed to load chart data. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const processDataForChart = () => {
    if (!selectedCategory || chartData.length === 0) {
      return;
    }

    try {
      const categoryCounts = {};
      
      chartData.forEach(row => {
        const rawValue = row[selectedCategory] || 'Unknown';
        const value = formatBusinessText(rawValue);
        categoryCounts[value] = (categoryCounts[value] || 0) + 1;
      });

      const processed = Object.entries(categoryCounts)
        .map(([name, value]) => ({
          name: name,
          value: value,
          percentage: ((value / chartData.length) * 100).toFixed(1)
        }))
        .sort((a, b) => b.value - a.value);

      let limitedData = processed.slice(0, 10);
      
      if (processed.length > 10) {
        const othersSum = processed.slice(10).reduce((sum, item) => sum + item.value, 0);
        const othersPercentage = ((othersSum / chartData.length) * 100).toFixed(1);
        
        limitedData.push({
          name: 'Others',
          value: othersSum,
          percentage: othersPercentage
        });
      }

      setProcessedData(limitedData);
    } catch (error) {
      console.error('Error processing data:', error);
      setError('Error processing data for chart.');
    }
  };

  useEffect(() => {
    if (selectedCategory && chartData.length > 0) {
      processDataForChart();
    }
  }, [selectedCategory, chartData]);

  const handleAddToDashboard = () => {
    if (processedData.length > 0 && widgetName.trim()) {
      const widgetData = [{
        recommendation: widgetName.trim() || `Pie Chart: ${selectedCategory} Distribution`,
        notWorkingWell: `Shows distribution across ${processedData.length} categories`,
        widgetName: widgetName.trim(),
        widgetType: 'pie_chart',
        cardType: 'Build Pie Chart',
        selectedColumns: [selectedCategory],
        query: JSON.stringify({ showValueType, decimalPoints, customColors })
      }];
      
      onAddToDashboard(widgetData);
      onClose();
    }
  };

  const renderChart = () => {
    if (processedData.length === 0) {
      return (
        <Box className="pie-chart-empty">
          <Typography variant="caption" color="text.secondary">
            Select a category to generate pie chart
          </Typography>
        </Box>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart margin={{ top: 50, right: 30, bottom: 50, left: 30 }}>
          <Pie
            data={processedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percentage, value }) => {
              const displayVal = showValueType === 'percentage' ? percentage : value;
              const formattedVal = Number(displayVal).toFixed(decimalPoints);
              return `${name}: ${formattedVal}${showValueType === 'percentage' ? '%' : ''}`;
            }}
            outerRadius={90}
            fill="#8884d8"
            dataKey="value"
          >
            {processedData.map((entry, index) => {
              const color = customColors[entry.name] || COLORS[index % COLORS.length];
              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Pie>
          <Tooltip 
            formatter={(value, name) => [
              value,
              `${selectedCategory} Count`
            ]}
            labelFormatter={(label) => `${selectedCategory}: ${label}`}
          />
          <Legend 
            verticalAlign="bottom"
            align="center"
            layout="horizontal"
            wrapperStyle={{ paddingTop: '20px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={branding.dialog?.PaperProps}
      slotProps={branding.dialog?.slotProps}
      className="build-pie-chart-dialog"
    >
      <DialogTitle className="build-pie-chart-title">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box className="build-pie-chart-title-content">
            <PieChartIcon className="build-pie-chart-icon" />
            <Typography variant="h6" component="span">Build Pie Chart</Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="build-pie-chart-content">
        {loading && (
          <Box className="build-pie-chart-loading">
            <CircularProgress />
          </Box>
        )}


        {!loading && !error && (
          <>
            <Grid container spacing={2} className="build-pie-chart-inputs" sx={{ alignItems: 'flex-start' }}>
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', fontFamily: branding.fonts.primary }}
                  >
                    Card Name
                    <Box component="span" sx={{ color: '#666666', ml: 0.25 }}>*</Box>:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  value={widgetName}
                  onChange={(e) => setWidgetName(e.target.value)}
                  placeholder="Enter a name for this pie chart card"
                  variant="outlined"
                  required
                  helperText="Give your card a descriptive name"
                  sx={{ mt: 0 }}
                  inputProps={{ 'aria-label': 'Card Name' }}
                  InputProps={{
                    startAdornment: <PieChartIcon sx={{ mr: 1, color: '#000000', fontSize: '1.25rem' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                    Category to Analyze:
                  </Typography>
                </Box>
                <FormControl fullWidth sx={{ mt: 0 }}>
                  <InputLabel>Category to Analyze</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Category to Analyze"
                    MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                  >
                    {columns.map((column) => (
                      <MenuItem key={column} value={column}>
                        {column}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                    Show Value As:
                  </Typography>
                </Box>
                <FormControl fullWidth sx={{ mt: 0 }}>
                  <InputLabel>Show Value As</InputLabel>
                  <Select
                    value={showValueType}
                    onChange={(e) => setShowValueType(e.target.value)}
                    label="Show Value As"
                    MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="value">Value</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                    Decimal Points:
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  fullWidth
                  value={decimalPoints}
                  onChange={(e) => setDecimalPoints(Math.max(0, parseInt(e.target.value) || 0))}
                  inputProps={{ min: 0, max: 4 }}
                  variant="outlined"
                  sx={{ mt: 0 }}
                />
              </Grid>
              {processedData.length > 0 && (
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', mb: 1 }}>
                    Customize Slice Colors:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                    {processedData.map((entry, index) => {
                      const currentColor = customColors[entry.name] || COLORS[index % COLORS.length];
                      return (
                        <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={entry.name}>
                            {entry.name}
                          </Typography>
                          <Select
                            size="small"
                            value={currentColor}
                            onChange={(e) => setCustomColors(prev => ({ ...prev, [entry.name]: e.target.value }))}
                            renderValue={(value) => (
                              <Box sx={{ width: 16, height: 16, backgroundColor: value, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                            )}
                            sx={{
                              width: 60,
                              height: 28,
                              '& .MuiSelect-select': { pl: 1, pr: '28px !important', py: 0, display: 'flex', alignItems: 'center', height: '100%' },
                              '& .MuiSelect-icon': { right: 4 },
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              bgcolor: 'transparent',
                              boxShadow: 'none'
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                          >
                            {COLORS.map((color) => (
                              <MenuItem key={color} value={color} sx={{ justifyContent: 'center', p: 0.5 }}>
                                <Box sx={{ width: 16, height: 16, backgroundColor: color, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.1)' }} />
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              )}
            </Grid>
            {renderChart()}

            {processedData.length > 0 && (
              <Box className="build-pie-chart-summary">
                <Typography variant="subtitle2" gutterBottom>
                  Data Summary:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • Total records: {chartData.length}<br/>
                  • Categories shown: {processedData.length}<br/>
                  • Most common: {processedData[0]?.name} ({processedData[0]?.percentage}%)<br/>
                  • Least common: {processedData[processedData.length - 1]?.name} ({processedData[processedData.length - 1]?.percentage}%)
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className="build-pie-chart-actions">
        <Button variant={branding.button.variant} size={branding.button.size} sx={branding.button.sx} onClick={onClose}>
          Cancel
        </Button>
        {editWidgetId && onUpdateCard ? (
          <Button
            variant={branding.button.variant}
            size={branding.button.size}
            sx={branding.button.sx}
            onClick={() => onUpdateCard({ widgetName: widgetName.trim(), selectedColumns: selectedCategory ? [selectedCategory] : [], widgetType: 'piechart', description: `Shows distribution across ${processedData.length} categories`, query: JSON.stringify({ showValueType, decimalPoints, customColors }) })}
            disabled={!widgetName.trim() || !selectedCategory}
          >
            Update card
          </Button>
        ) : (
          <Button 
            variant={branding.button.variant}
            size={branding.button.size}
            sx={branding.button.sx}
            onClick={handleAddToDashboard}
            disabled={processedData.length === 0 || !widgetName.trim()}
          >
            {initialData ? 'Edit Card' : 'Add Card'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BuildPieChart;
